// Product Selection page - allows creators to browse and select products from a catalog

import {
  NoCatalogueOrderFlow,
  OrderApprovedConfirmation,
  ProductCard,
  ProductCategoryFilter,
  ProductDetailView,
  ProductGrid,
  ProductSelectionHeader,
  isProductOutOfStock,
  type ContactInfo,
  type EmptyReason,
  type ProductCardData,
} from "@/components/products";
import { ConditionsSummary } from "@/components/products/conditions-summary";
import {
  computeProductConditionStatuses,
  getMatchingConditions,
  type ConditionCriteria,
} from "@/lib/conditions";
import {
  useProductSelectionState,
  type Catalog,
  type NoCatalogueConfig,
  type OrderRequest,
  type ProductDetails,
  type ProductVariant,
  type SelectionRule,
} from "@/lib/hooks/use-product-selection";
import type { ProfileAddressLike } from "@/lib/hooks/use-product-selection-types";
import {
  useMemberDataQuery,
  type MemberAddress,
  type MemberDataResponse,
} from "@/lib/queries/member-data";
import { useOrderRequestQuery, type CartItem } from "@/lib/queries/products";
import { useProfileQuery } from "@/lib/queries/profile";
import { useProjectInteractionsQuery } from "@/lib/queries/projects";
import type { CampaignRouteContext } from "@/lib/types/route-context";
import { extractOrderKey, extractRequestId } from "@/lib/url-utils";
import { Button, Card, CardContent, Input, Skeleton } from "@repo/ui";
import { createFileRoute } from "@tanstack/react-router";
import { sanitize } from "@/lib/dom-purify";
import {
  ClipboardList,
  RefreshCw,
  Search,
  SlidersHorizontal,
  Star,
  X,
} from "lucide-react";
import {
  useCallback,
  useMemo,
  type ChangeEvent,
  type Dispatch,
  type SetStateAction,
} from "react";

// Main Route Component (Container)
export const Route = createFileRoute(
  "/(org)/o/$orgSlug/p/$profileSlug/c/$campaignId/product-selection/$interactionId",
)({
  component: ProductSelectionPageContainer,
});

interface CreatorInteraction {
  id: string;
  secureUrl?: string | null;
  taskType: string;
  title: string;
  status: string;
}

function ProductSelectionPageContainer() {
  const { interactionId } = Route.useParams();
  const context = Route.useRouteContext() as CampaignRouteContext;
  const { programId, profile, organization } = context;
  const organizationId = organization.id;

  // Fetch interactions to get the secureUrl for this interaction
  const { data: interactionsData, isPending: interactionsPending } =
    useProjectInteractionsQuery(organizationId, programId, profile.id);

  // Find the specific interaction by ID to get its secureUrl
  const interaction = interactionsData?.interactions.find(
    (i: CreatorInteraction) => i.id === interactionId,
  );

  // Extract the order key (base64-encoded JWT) from the secureUrl
  const secureUrl = interaction?.secureUrl ?? "";
  const orderKey = extractOrderKey(secureUrl) ?? "";

  // Extract requestId for member data
  const requestId = useMemo(() => {
    const directId = extractRequestId(secureUrl);
    if (directId) return directId;
    if (!interactionsData?.interactions) return null;
    for (const i of interactionsData.interactions as CreatorInteraction[]) {
      if (i.secureUrl) {
        const id = extractRequestId(i.secureUrl);
        if (id) return id;
      }
    }
    return null;
  }, [secureUrl, interactionsData]);

  // Cart persistence key scoped to this specific order interaction
  const cartStorageKey = `cart-${organizationId}-${interactionId}`;

  // Fetch order request
  const { data: orderRequest, isPending: isOrderPendingRaw } =
    useOrderRequestQuery(organizationId, orderKey, programId);
  const isOrderPending = !!orderKey && isOrderPendingRaw;

  // Fetch member data for shipping address
  const { data: memberData } = useMemberDataQuery(
    organization.id,
    requestId ?? "",
  );

  // Fetch profile for address selector
  const { data: profileData } = useProfileQuery(organization.id, profile.id);

  // Shared product selection state
  const hookResult = useProductSelectionState({
    organizationId,
    orderKey,
    programId,
    cartStorageKey,
    orderRequest: (orderRequest ?? null) as OrderRequest | null,
    isOrderPending,
    clientId: orderRequest?.clientId ?? "",
    memberData: (memberData ?? undefined) as MemberDataResponse | undefined,
    profileAddresses: profileData?.addresses,
    profileEmail: profileData?.email,
    profileId: profile.id,
    onSubmitSuccess: () => {
      // Intentionally empty: product-selection stays on page after order submission.
      // Boost-product-selection navigates back; this flow does not.
    },
    analyticsPrefix: "product",
    analyticsExtraProps: { interactionId },
    supportsNoCatalogue: true,
  });

  // Keep skeleton visible until products actually arrive to prevent
  // a flash of "No products found" during the initial fetch (EPD-6052).
  const isLoading =
    interactionsPending ||
    hookResult.isLoading ||
    (hookResult.isFetching && hookResult.products.length === 0);

  return (
    <ProductSelectionPageView
      key={`${organizationId}-${interactionId}`}
      interactionId={interactionId}
      {...hookResult}
      isLoading={isLoading}
      profileId={profile.id}
    />
  );
}

// Exported View Component for Storybook
export interface ProductSelectionPageViewProps {
  interactionId: string;
  organizationId: string;
  isLoading: boolean;
  orderKey: string;
  orderRequest: OrderRequest | undefined;
  isProductRequestForm: boolean;
  isShopifyType: boolean;
  catalog: Catalog | undefined;
  noCatalogueProjectConfig: NoCatalogueConfig | null;
  instructionText?: string | null;
  quantityMax?: number | null;
  effectiveSelectionRule: SelectionRule | undefined;
  currencyCode: string;
  cart: CartItem[];
  memberData: MemberDataResponse | undefined;
  memberAddress: MemberAddress | null;
  profileAddresses?: ProfileAddressLike[];
  memberEmail: string | null;
  isSubmitting: boolean;
  onUpdateQuantity: (id: string, quantity: number) => void;
  onRemoveItem: (id: string) => void;
  onAddressChange: (address: MemberAddress) => void;
  onSubmitOrder: () => void;
  isCartOpen: boolean;
  onOpenChange: Dispatch<SetStateAction<boolean>>;

  showPrice: boolean;
  showVendor?: boolean;
  showTags?: boolean;
  searchQuery: string;
  setSearchQuery: (q: string) => void;
  isFilterOpen: boolean;
  onFilterOpenChange: (open: boolean) => void;
  activeFilterCount: number;
  pendingCategories: Set<string>;
  availableCategories: string[];
  onToggleCategory: (category: string) => void;
  onRemoveCategory: (category: string) => void;
  onClearFilters: () => void;
  onApplyFilters: () => void;
  selectedCategories: Set<string>;
  filteredProducts: ProductCardData[];
  selectedProductId: number | null;
  onProductClick: (product: ProductCardData) => void;
  onCloseProductDetail: () => void;
  selectedProductFilterLabels: string[];
  onApplyFilterFromDetail: (label: string) => void;
  productDetails: ProductDetails | undefined;
  isProductDetailsPending?: boolean;
  productDetailsError?: Error | null;
  onAddToCart: (variant: ProductVariant, qty?: number) => void;
  hasNextPage: boolean;
  allowAutoPagination?: boolean;
  fetchNextPage: () => void;
  isFetchingNextPage: boolean;
  isProductsError?: boolean;
  /** The Shopify resource (store/order) is permanently unavailable. */
  isStoreUnavailable?: boolean;
  refetchProducts?: () => void;
  /** Short hash code for support to correlate with Datadog errors. */
  supportCode?: string | null;
  cartQuantity: number;
  products: ProductCardData[];
  featuredProducts?: ProductCardData[];
  showFeaturedProducts?: boolean;
  contactInfoMode?: "same" | "different";
  onContactInfoModeChange?: (mode: "same" | "different") => void;
  contactInfo?: ContactInfo;
  onContactInfoChange?: (info: ContactInfo) => void;
  /** Whether the selected address has all required contact fields. */
  contactInfoComplete?: boolean;
  /** Verified profile email for the NoCatalogue flow's effective email logic (EPD-5898). */
  profileEmail?: string | null;
  /** Profile ID for saving phone back to profile address. */
  profileId?: string;
  /** Condition criteria extracted from the selection rule for badge highlighting. */
  conditionCriteria?: ConditionCriteria;
  /** Index of the currently active condition filter (null = no filter). */
  activeConditionIndex?: number | null;
  /** Toggle a condition filter by index. */
  onConditionClick?: (index: number) => void;
  /** Inline validation errors from the last submit attempt. */
  submitErrors?: Partial<
    Record<"address" | "contact" | "cart" | "submit", string>
  >;
  /** Validation error from add-to-cart or update-quantity operations. */
  cartValidationError?: string | null;
  /** Minimum stock level from brand inventory settings. */
  minimumStockLevel?: number;
}

export function ProductSelectionPageView(props: ProductSelectionPageViewProps) {
  const {
    interactionId,
    organizationId,
    isLoading,
    orderKey,
    orderRequest,
    isProductRequestForm,
    isShopifyType,
    catalog,
    quantityMax,
    effectiveSelectionRule,
    currencyCode,
    cart,
    memberData,
    memberAddress,
    profileAddresses,
    memberEmail,
    isSubmitting,
    onUpdateQuantity,
    onRemoveItem,
    onAddressChange,
    onSubmitOrder,
    isCartOpen,
    onOpenChange,
    showPrice,
    showVendor = true,
    showTags = true,
    searchQuery,
    setSearchQuery,
    isFilterOpen,
    onFilterOpenChange,
    activeFilterCount,
    pendingCategories,
    availableCategories,
    onToggleCategory,
    onRemoveCategory,
    onClearFilters,
    onApplyFilters,
    selectedCategories,
    filteredProducts,
    selectedProductId,
    onProductClick,
    onCloseProductDetail,
    selectedProductFilterLabels,
    onApplyFilterFromDetail,
    productDetails,
    isProductDetailsPending = false,
    productDetailsError = null,
    onAddToCart,
    hasNextPage,
    allowAutoPagination = true,
    fetchNextPage,
    isFetchingNextPage,
    isProductsError,
    isStoreUnavailable,
    refetchProducts,
    supportCode,
    cartQuantity,
    products,
    featuredProducts,
    showFeaturedProducts,
    contactInfoMode,
    onContactInfoModeChange,
    contactInfo,
    onContactInfoChange,
    contactInfoComplete,
    noCatalogueProjectConfig,
    instructionText,
    profileEmail,
    profileId,
    conditionCriteria,
    activeConditionIndex,
    onConditionClick,
    submitErrors,
    cartValidationError,
    minimumStockLevel = 0,
  } = props;

  // Derived state inside view
  const cartVariantIds = useMemo(
    () => new Set(cart.map((item) => item.variantId)),
    [cart],
  );

  // Lookup the selected product once instead of repeating products.find()
  const selectedProduct = useMemo(
    () =>
      selectedProductId != null
        ? products.find((p) => p.catalogProductId === selectedProductId)
        : undefined,
    [products, selectedProductId],
  );

  // Map from product ID → total quantity in cart (across all variants)
  const cartQuantityByProductId = useMemo(() => {
    const map = new Map<number, number>();
    for (const item of cart) {
      if (item.productId != null) {
        map.set(item.productId, (map.get(item.productId) ?? 0) + item.quantity);
      }
    }
    return map;
  }, [cart]);

  // Map from variant ID → cart item info (for in-place editing on detail view)
  const variantCartInfo = useMemo(() => {
    const map = new Map<number, { cartItemId: string; quantity: number }>();
    for (const item of cart) {
      map.set(item.variantId, { cartItemId: item.id, quantity: item.quantity });
    }
    return map;
  }, [cart]);

  // Compute which unfulfilled conditions the selected product satisfies
  const matchingConditionNames = useMemo(() => {
    if (!selectedProduct || !effectiveSelectionRule?.conditions?.length)
      return undefined;
    const conditions = effectiveSelectionRule.conditions;
    const statuses = computeProductConditionStatuses(cart, conditions);
    // Only show conditions that the product matches AND are not yet fulfilled
    const matching = getMatchingConditions(selectedProduct, conditions);
    return matching
      .filter((c) => {
        const status = statuses.find((s) => s.condition === c);
        return status && !status.fulfilled;
      })
      .map((c) => c.conditionName);
  }, [selectedProduct, effectiveSelectionRule?.conditions, cart]);

  // Remove all cart items for a product (used by the trash icon on product cards)
  const handleRemoveProductFromCart = useCallback(
    (product: ProductCardData) => {
      for (const item of cart) {
        if (item.productId === product.id) {
          onRemoveItem(item.id);
        }
      }
    },
    [cart, onRemoveItem],
  );

  // Determine why the product list might be empty for contextual messaging.
  const emptyReason: EmptyReason = useMemo(() => {
    if (isStoreUnavailable) return "store-unavailable";

    const allOutOfStock =
      products.length > 0 && products.every((p) => isProductOutOfStock(p));

    if (allOutOfStock) return "all-out-of-stock";
    if (products.length > 0 && filteredProducts.length === 0)
      return "no-matches";
    return "no-products";
  }, [isStoreUnavailable, products, filteredProducts]);

  const bannerDescription =
    effectiveSelectionRule?.description || instructionText;
  const safeBannerHtml = useMemo(
    () => (bannerDescription ? sanitize(bannerDescription) : ""),
    [bannerDescription],
  );

  if (isLoading) {
    return (
      <div className="px-6 pb-6 space-y-6">
        {/* Skeleton without header (now in layout) */}
        <Skeleton className="h-[200px] w-full rounded-lg" />
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {[...Array(8)].map((_, i) => (
            // eslint-disable-next-line @eslint-react/no-array-index-key -- This is a static skeleton list
            <Skeleton key={i} className="h-[300px] w-full rounded-lg" />
          ))}
        </div>
      </div>
    );
  }

  // Check if we have the order key from secureUrl
  if (!orderKey || !orderRequest) {
    return (
      <div className="px-4 sm:px-6 py-4 sm:py-6">
        <Card className="mt-4">
          <CardContent className="py-8 text-center">
            <p className="text-muted-foreground">
              Product selection is not available. The secure URL could not be
              found.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // No Catalogue / Product Request Form (Typeform embed usually)
  if (isProductRequestForm) {
    // Ensure formFields is always an array to prevent crashes
    const safeNoCatalogueConfig: NoCatalogueConfig = {
      ...(noCatalogueProjectConfig ?? {}),
      formFields: noCatalogueProjectConfig?.formFields ?? [],
    };

    return (
      <NoCatalogueOrderFlow
        noCatalogueConfig={safeNoCatalogueConfig}
        instructionText={instructionText}
        interactionId={interactionId}
        organizationId={organizationId}
        orderKey={orderKey}
        orderRequest={
          orderRequest as {
            id: string;
            orderRequestStatus?: string | null;
            member?: { id: number; name?: string; email?: string } | null;
          }
        }
        memberData={memberData}
        profileAddresses={profileAddresses}
        profileEmail={profileEmail}
        profileId={profileId}
        quantityMax={quantityMax}
      />
    );
  }

  // ProductCatalog / Shopify: show confirmation for already-submitted orders
  const isAlreadySubmitted =
    orderRequest.orderRequestStatus === "PENDING" ||
    orderRequest.orderRequestStatus === "ACCEPTED";

  if (isAlreadySubmitted) {
    return (
      <OrderApprovedConfirmation
        orderRequestStatus={orderRequest.orderRequestStatus ?? "PENDING"}
        creatorOrderRequest={orderRequest.creatorOrderRequest ?? null}
        details={orderRequest.details ?? null}
        addedLineItems={orderRequest.addedLineItems ?? []}
        showPrice={showPrice}
      />
    );
  }

  if (!catalog && !isShopifyType && !orderRequest?.clientId) {
    return (
      <div className="px-4 sm:px-6 py-4 sm:py-6">
        <Card className="mt-4">
          <CardContent className="py-8 text-center">
            <p className="text-muted-foreground">
              Product selection is not available. The catalog could not be
              found.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 min-h-screen pb-20 [overflow-x:clip]">
      <ProductSelectionHeader
        effectiveSelectionRule={effectiveSelectionRule}
        currencyCode={currencyCode}
        cart={cart}
        memberAddress={memberAddress}
        profileAddresses={profileAddresses}
        memberEmail={memberEmail}
        isSubmitting={isSubmitting}
        onUpdateQuantity={onUpdateQuantity}
        onRemoveItem={onRemoveItem}
        onAddressChange={onAddressChange}
        onSubmitOrder={onSubmitOrder}
        isCartOpen={isCartOpen}
        onOpenChange={onOpenChange}
        showPrice={showPrice}
        showVendor={showVendor}
        showTags={showTags}
        contactInfoMode={contactInfoMode}
        onContactInfoModeChange={onContactInfoModeChange}
        contactInfo={contactInfo}
        onContactInfoChange={onContactInfoChange}
        contactInfoComplete={contactInfoComplete}
        isShopifyType={isShopifyType}
        submitErrors={submitErrors}
        cartValidationError={cartValidationError}
        minimumStockLevel={minimumStockLevel}
      ></ProductSelectionHeader>

      {/* Banner Information */}
      {safeBannerHtml && (
        <div className="px-4 sm:px-6">
          <div className="bg-accent border border-border rounded-lg p-3">
            <div className="flex items-start gap-3">
              <ClipboardList className="h-5 w-5 text-accent-foreground mt-0.5 shrink-0" />
              <div>
                <h3 className="font-medium text-accent-foreground text-sm">
                  Instructions
                </h3>
                <div
                  className="text-sm text-muted-foreground mt-1 whitespace-pre-wrap prose prose-sm max-w-none dark:prose-invert"
                  // eslint-disable-next-line @eslint-react/dom/no-dangerously-set-innerhtml -- sanitized with DOMPurify
                  dangerouslySetInnerHTML={{
                    __html: safeBannerHtml,
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Conditions Summary */}
      {effectiveSelectionRule && (
        <div className="px-4 sm:px-6">
          <ConditionsSummary
            selectionRule={effectiveSelectionRule}
            cart={cart}
            currencyCode={currencyCode}
            showPrice={showPrice}
            isShopifyType={isShopifyType}
            onConditionClick={onConditionClick}
            activeConditionIndex={activeConditionIndex}
          />
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 px-4 sm:px-6 w-full min-w-0 space-y-4">
        {/* Rejection banner */}
        {orderRequest?.orderRequestStatus === "REJECTED" && (
          <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-300 dark:border-amber-700 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-amber-100 dark:bg-amber-900/50 shrink-0">
                <RefreshCw className="h-4 w-4 text-amber-600 dark:text-amber-400" />
              </div>
              <div>
                <h3 className="font-semibold text-amber-900 dark:text-amber-200">
                  New Selection Needed
                </h3>
                <p className="text-sm text-amber-700 dark:text-amber-300 mt-1">
                  Your previous product selection wasn&apos;t quite right for
                  this campaign. No worries — please browse the catalog below
                  and submit a new selection!
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Search & Filter */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search products..."
              value={searchQuery}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                setSearchQuery(e.target.value)
              }
              className="pl-9"
            />
          </div>
          {availableCategories.length > 0 && (
            <Button
              variant="outline"
              className="gap-2 shrink-0"
              onClick={() => onFilterOpenChange(true)}
            >
              <SlidersHorizontal className="h-4 w-4" />
              Filter
              {activeFilterCount > 0 && (
                <span className="ml-1 flex h-5 w-5 items-center justify-center rounded-full bg-primary text-xs text-primary-foreground">
                  {activeFilterCount}
                </span>
              )}
            </Button>
          )}
        </div>

        {/* Selected Categories Tags */}
        {selectedCategories.size > 0 && (
          <div className="flex flex-wrap gap-2">
            {Array.from(selectedCategories).map((category) => (
              <Button
                key={category}
                variant="secondary"
                size="sm"
                className="h-7 text-xs"
                onClick={() => onRemoveCategory(category)}
              >
                {category}
                <X className="ml-1 h-3 w-3" />
              </Button>
            ))}
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-xs text-muted-foreground"
              onClick={onClearFilters}
            >
              Clear all
            </Button>
          </div>
        )}

        {/* Featured Products — horizontal scroll row */}
        {showFeaturedProducts &&
          featuredProducts &&
          featuredProducts.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Star className="h-5 w-5 text-amber-500 fill-amber-500" />
                <h2 className="text-lg font-semibold text-foreground">
                  Featured
                </h2>
              </div>
              <div className="flex gap-4 overflow-x-auto pb-2 -mx-1 px-1 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
                {featuredProducts.map((product) => (
                  <div key={product.id} className="w-48 shrink-0">
                    <ProductCard
                      product={product}
                      onClick={onProductClick}
                      showPrice={showPrice}
                      showVendor={showVendor}
                      showTags={showTags}
                      conditionCriteria={conditionCriteria}
                      cartQuantity={cartQuantityByProductId.get(product.id)}
                      onRemoveFromCart={handleRemoveProductFromCart}
                      minimumStockLevel={minimumStockLevel}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

        {/* Product Grid */}
        <ProductGrid
          products={filteredProducts}
          onProductClick={onProductClick}
          showPrice={showPrice}
          showVendor={showVendor}
          showTags={showTags}
          hasMore={!isProductsError && hasNextPage && allowAutoPagination}
          isFetchingMore={isFetchingNextPage}
          onLoadMore={fetchNextPage}
          isError={isProductsError}
          onRetry={refetchProducts}
          supportCode={supportCode}
          emptyReason={emptyReason}
          conditionCriteria={conditionCriteria}
          cartQuantityByProductId={cartQuantityByProductId}
          onRemoveFromCart={handleRemoveProductFromCart}
          minimumStockLevel={minimumStockLevel}
        />
      </div>

      {/* Product Detail - full page view */}
      {selectedProductId !== null && (
        <ProductDetailView
          key={selectedProductId}
          product={productDetails?.product}
          isLoading={isProductDetailsPending}
          error={productDetailsError}
          showPrice={showPrice}
          showVendor={showVendor}
          showTags={showTags}
          currencyCode={currencyCode}
          cartVariantIds={cartVariantIds}
          totalInventory={selectedProduct?.totalInventory}
          tracksInventory={selectedProduct?.tracksInventory}
          onAddToCart={onAddToCart}
          onBack={onCloseProductDetail}
          maxQuantity={effectiveSelectionRule?.maxQuantity}
          currentCartQuantity={cartQuantity}
          filterLabels={selectedProductFilterLabels}
          onApplyFilter={onApplyFilterFromDetail}
          vendor={selectedProduct?.vendor}
          tags={selectedProduct?.tags}
          productType={selectedProduct?.productType}
          conditionCriteria={conditionCriteria}
          variantCartInfo={variantCartInfo}
          onUpdateCartQuantity={onUpdateQuantity}
          onRemoveCartItem={onRemoveItem}
          matchingConditionNames={matchingConditionNames}
          minimumStockLevel={minimumStockLevel}
          catalogInventoryRemaining={(() => {
            const limit = productDetails?.maxInventoryLimit;
            if (!limit) return undefined;
            const ordered = productDetails?.currentOrderedQuantity ?? 0;
            const inCart = cart
              .filter((item) => item.catalogProductId === selectedProductId)
              .reduce((sum, item) => sum + item.quantity, 0);
            return Math.max(0, limit - ordered - inCart);
          })()}
          maxInventoryLimit={productDetails?.maxInventoryLimit ?? undefined}
        />
      )}

      {/* Category Filter Dialog / Drawer */}
      <ProductCategoryFilter
        open={isFilterOpen}
        onOpenChange={onFilterOpenChange}
        categories={availableCategories}
        selectedCategories={pendingCategories}
        onToggleCategory={onToggleCategory}
        onClear={onClearFilters}
        onApply={onApplyFilters}
      />
    </div>
  );
}
